import React, { useCallback, useEffect, useState } from 'react';
import { Compass, Lock, Activity, ChartBar, HardDrives, Globe, ArrowRight } from 'phosphor-react';
import { OptionsPageShell } from '../../components/options/OptionsPageShell';
import { useOptionsLocale } from '../../context/OptionsLanguageContext';
import { useOptionsEntitlements } from '../../context/OptionsEntitlementsContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '../../lib/api';
import GeniLoader from '../../components/GeniLoader';
import { OptionsProLockedFeature } from '../../components/options/OptionsProLockedFeature';

// Mapeamento centralizado de localization para descrições, exposições e estratégias do BTC Outlook
const LOCALIZED_MAPPINGS = {
  pt: {
    descriptions: {
      BULLISH: "Nossa leitura atual favorece uma exposição compatível com o cenário de alta ao Bitcoin para os próximos 30–60 dias.",
      SLIGHTLY_BULLISH: "Nossa leitura atual favorece uma exposição moderadamente positiva (compatível com o cenário) ao Bitcoin para os próximos 30–60 dias.",
      NEUTRAL: "Nossa leitura atual favorece uma exposição neutra ao Bitcoin (compatível com o cenário) para os próximos 30–60 dias.",
      SLIGHTLY_BEARISH: "Nossa leitura atual favorece uma exposição moderadamente negativa (compatível com o cenário) ao Bitcoin para os próximos 30–60 dias.",
      BEARISH: "Nossa leitura atual favorece uma exposição negativa ao Bitcoin (compatível com o cenário) para os próximos 30–60 dias."
    },
    exposures: {
      "Positive": "Positiva",
      "Slightly Positive": "Moderadamente Positiva",
      "Neutral": "Neutra",
      "Slightly Negative": "Moderadamente Negativa",
      "Negative": "Negativa"
    },
    strategies: {
      "Short Strangle com viés bullish": "Short Strangle com viés bullish",
      "Short Strangle com viés bearish": "Short Strangle com viés bearish",
      "Long Call": "Long Call",
      "Bull Call Spread": "Bull Call Spread",
      "Bull Put Spread": "Bull Put Spread",
      "Short Strangle": "Short Strangle",
      "Iron Condor": "Iron Condor",
      "Long Put": "Long Put",
      "Bear Put Spread": "Bear Put Spread",
      "Bear Call Spread": "Bear Call Spread"
    }
  },
  en: {
    descriptions: {
      BULLISH: "Our current outlook favors a Bitcoin exposure compatible with the bullish scenario over the next 30–60 days.",
      SLIGHTLY_BULLISH: "Our current outlook favors moderately positive Bitcoin exposure, compatible with the current scenario, over the next 30–60 days.",
      NEUTRAL: "Our current outlook favors neutral Bitcoin exposure, compatible with the current scenario, over the next 30–60 days.",
      SLIGHTLY_BEARISH: "Our current outlook favors moderately negative Bitcoin exposure, compatible with the current scenario, over the next 30–60 days.",
      BEARISH: "Our current outlook favors negative Bitcoin exposure, compatible with the current scenario, over the next 30–60 days."
    },
    exposures: {
      "Positive": "Positive",
      "Slightly Positive": "Slightly Positive",
      "Neutral": "Neutral",
      "Slightly Negative": "Slightly Negative",
      "Negative": "Negative"
    },
    strategies: {
      "Short Strangle com viés bullish": "Short Strangle with bullish bias",
      "Short Strangle com viés bearish": "Short Strangle with bearish bias",
      "Long Call": "Long Call",
      "Bull Call Spread": "Bull Call Spread",
      "Bull Put Spread": "Bull Put Spread",
      "Short Strangle": "Short Strangle",
      "Iron Condor": "Iron Condor",
      "Long Put": "Long Put",
      "Bear Put Spread": "Bear Put Spread",
      "Bear Call Spread": "Bear Call Spread"
    }
  }
};

export default function BtcOutlook() {
  const { t, isPortuguese } = useOptionsLocale();
  const { isPro, loading: entitlementsLoading, openUpgradeModal } = useOptionsEntitlements();
  const [outlook, setOutlook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Alert Toggle State
  const [alertEnabled, setAlertEnabled] = useState(false);
  const [togglingAlert, setTogglingAlert] = useState(false);

  const fetchOutlook = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/options/btc-outlook');
      setOutlook(response.data);
      setAlertEnabled(response.data.alert_enabled === true);
    } catch (err) {
      console.error('Error fetching BTC Outlook:', err);
      setError(isPortuguese ? 'Falha ao carregar o BTC Outlook' : 'Failed to load BTC Outlook');
    } finally {
      setLoading(false);
    }
  }, [isPortuguese]);

  useEffect(() => {
    if (entitlementsLoading) return;
    if (!isPro) {
      setLoading(false);
      return;
    }
    fetchOutlook();
  }, [entitlementsLoading, fetchOutlook, isPro]);

  const handleToggleAlert = async () => {
    if (!isPro) {
      openUpgradeModal("BTC_OUTLOOK");
      return;
    }
    try {
      setTogglingAlert(true);
      const nextState = !alertEnabled;
      const response = await api.post('/options/btc-outlook/alert', { enabled: nextState });
      if (response.data.success) {
        setAlertEnabled(nextState);
        toast.success(nextState 
          ? (isPortuguese ? "Alerta de alteração ativado!" : "Outlook change alert activated!")
          : (isPortuguese ? "Alerta de alteração desativado!" : "Outlook change alert deactivated!")
        );
      }
    } catch (err) {
      console.error('Error toggling outlook alert:', err);
      toast.error(isPortuguese ? 'Erro ao configurar alerta' : 'Failed to configure alert');
    } finally {
      setTogglingAlert(false);
    }
  };

  const getDirectionArrow = (bias) => {
    if (!bias) return '→';
    const b = bias.toUpperCase();
    if (b === 'BULLISH') return '↑';
    if (b === 'SLIGHTLY_BULLISH') return '↗';
    if (b === 'BEARISH') return '↓';
    if (b === 'SLIGHTLY_BEARISH') return '↘';
    return '→';
  };

  const localeKey = isPortuguese ? 'pt' : 'en';
  const getLocalizedDescription = (bias) => {
    if (!bias) return '';
    return LOCALIZED_MAPPINGS[localeKey].descriptions[bias] || outlook?.description || '';
  };

  const getLocalizedExposure = (exp) => {
    if (!exp) return '';
    return LOCALIZED_MAPPINGS[localeKey].exposures[exp] || exp;
  };

  const getLocalizedStrategy = (strat) => {
    if (!strat) return '';
    return LOCALIZED_MAPPINGS[localeKey].strategies[strat] || strat;
  };

  const renderFourPilars = () => (
    <div className="space-y-4 pt-4 border-t border-white/5">
      <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-white/35">
        {isPortuguese ? 'COMO CONSTRUÍMOS NOSSO OUTLOOK' : 'HOW WE BUILD OUR OUTLOOK'}
      </h3>
      <p className="text-sm text-white/55 leading-relaxed">
        {isPortuguese 
          ? 'Nosso time analisa diferentes dimensões do mercado para sintetizar o cenário atual do Bitcoin.'
          : 'Our team analyzes different market dimensions to synthesize the current Bitcoin scenario.'}
      </p>
      
      <div className="grid gap-3 grid-cols-2 md:grid-cols-4 pt-2">
        <div className="rounded-[16px] border border-white/5 bg-zinc-950/20 p-4 space-y-2">
          <div className="text-[#A855F7]"><Activity size={20} weight="bold" /></div>
          <p className="text-xs font-black text-white/80 leading-tight">GEX</p>
          <p className="text-[10px] text-white/45 leading-relaxed">
            {isPortuguese ? 'Exposição Gamma' : 'Gamma Exposure'}
          </p>
        </div>
        <div className="rounded-[16px] border border-white/5 bg-zinc-950/20 p-4 space-y-2">
          <div className="text-[#A855F7]"><ChartBar size={20} weight="bold" /></div>
          <p className="text-xs font-black text-white/80 leading-tight">
            {isPortuguese ? 'ANÁLISE TÉCNICA' : 'TECHNICAL ANALYSIS'}
          </p>
          <p className="text-[10px] text-white/45 leading-relaxed">
            {isPortuguese ? 'Comportamento de Preço & Vol' : 'Price Action & Vol'}
          </p>
        </div>
        <div className="rounded-[16px] border border-white/5 bg-zinc-950/20 p-4 space-y-2">
          <div className="text-[#A855F7]"><HardDrives size={20} weight="bold" /></div>
          <p className="text-xs font-black text-white/80 leading-tight">ORDER FLOW & LIQ.</p>
          <p className="text-[10px] text-white/45 leading-relaxed">
            {isPortuguese ? 'Livro de Ofertas & Liquidez' : 'Order Book & Liquidity'}
          </p>
        </div>
        <div className="rounded-[16px] border border-white/5 bg-zinc-950/20 p-4 space-y-2">
          <div className="text-[#A855F7]"><Globe size={20} weight="bold" /></div>
          <p className="text-xs font-black text-white/80 leading-tight">
            {isPortuguese ? 'MACROECONOMIA' : 'MACROECONOMICS'}
          </p>
          <p className="text-[10px] text-white/45 leading-relaxed">
            {isPortuguese ? 'Juros & Liquidez Global' : 'Rates & Global Liquidity'}
          </p>
        </div>
      </div>
    </div>
  );

  const formatLastUpdatedDate = (isoStr) => {
    if (!isoStr) return '—';
    try {
      const d = new Date(isoStr);
      const months = isPortuguese 
        ? ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ']
        : ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
      const day = d.getUTCDate().toString().padStart(2, '0');
      const month = months[d.getUTCMonth()];
      const year = d.getUTCFullYear();
      const hours = d.getUTCHours().toString().padStart(2, '0');
      const minutes = d.getUTCMinutes().toString().padStart(2, '0');
      return `${day} ${month} ${year} · ${hours}:${minutes} UTC`;
    } catch {
      return '—';
    }
  };

  return (
    <OptionsPageShell hideHeader>
      <div className="min-h-[calc(100vh-180px)] w-full py-6 sm:py-8" data-testid="options-btc-outlook-page">
        <div className="w-full max-w-[800px] mx-auto space-y-6">
          
          {/* Header */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between border-b border-white/5 pb-4" data-testid="options-btc-outlook-header">
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-white/45">
                <Compass size={14} weight="bold" />
                <span>BTC OUTLOOK</span>
              </div>
              <h1 className="text-3xl font-black tracking-tight text-white flex items-center gap-2" data-typography="heading">
                {isPortuguese ? 'Viés do Bitcoin' : 'Bitcoin Market Outlook'}
                {!isPro && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-[#A855F7]/10 text-[#A855F7] border border-[#A855F7]/20 px-2 py-0.5 text-xs font-semibold">
                    <Lock size={11} weight="fill" /> PRO
                  </span>
                )}
              </h1>
              <p className="text-sm text-white/60">
                {isPortuguese 
                  ? 'Acompanhe nossa leitura estratégica consolidada de tendência e exposição compatível com o cenário para o mercado de opções de Bitcoin.'
                  : 'Track our consolidated strategic trend analysis and compatible exposure scenario for the Bitcoin options market.'}
              </p>
            </div>

            {/* Notification Toggle Control */}
            <div className="shrink-0">
              <button
                type="button"
                onClick={handleToggleAlert}
                disabled={togglingAlert}
                className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-bold transition-all duration-300 border ${
                  !isPro
                    ? 'border-white/5 bg-white/[0.02] text-white/45 hover:border-white/10 hover:text-white/60'
                    : alertEnabled
                      ? 'border-[#00E676]/20 bg-[#00E676]/10 text-[#00E676]'
                      : 'border-white/10 bg-white/[0.05] text-white/70 hover:bg-white/10 hover:text-white'
                }`}
                data-testid="options-btc-outlook-alert-toggle"
              >
                <span>🔔</span>
                <span>
                  {alertEnabled 
                    ? (isPortuguese ? 'Alerta ativo' : 'Alert active')
                    : (isPortuguese ? 'Alertar quando houver alteração' : 'Notify me when Outlook changes')}
                </span>
                {!isPro && <Lock size={11} className="text-[#A855F7] ml-0.5" weight="fill" />}
              </button>
            </div>
          </div>

          {!isPro && !entitlementsLoading ? (
            <OptionsProLockedFeature
              featureKey="BTC_OUTLOOK"
              title="BTC Outlook"
              description={isPortuguese
                ? 'Acesse o viés estratégico de 30–60 dias, exposições compatíveis e níveis de referência para Futures Hedge.'
                : 'Access the 30–60 day strategic bias, compatible exposures and Futures Hedge reference levels.'}
              testId="options-btc-outlook-pro-lock"
            />
          ) : loading ? (
            <div className="rounded-[24px] border border-white/5 bg-zinc-950/40 p-12 text-center">
              <GeniLoader size="md" />
            </div>
          ) : error ? (
            <div className="rounded-[24px] border border-white/5 bg-zinc-950/40 p-8 text-center text-red-400">
              {error}
            </div>
          ) : (
            <div className="space-y-6">
              
              {/* Published Content */}
              {outlook?.status === 'published' ? (
                <>
                  {isPro && outlook.entitled ? (
                    /* PRO EXPERIENCE */
                    <Card className="border border-white/5 bg-zinc-950/40 backdrop-blur-md rounded-[24px] text-white overflow-hidden shadow-none">
                      <CardContent className="p-6 sm:p-8 space-y-6">
                        
                        {/* Bias Header */}
                        <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-4 pb-4 border-b border-white/5">
                          <div className="space-y-1">
                            <p className="text-[10px] font-medium uppercase tracking-[0.22em] text-white/35">
                              {isPortuguese ? 'VIÉS DE MERCADO · 30–60 DIAS' : 'MARKET BIAS · 30–60 DAYS'}
                            </p>
                            <div className="flex items-center gap-2.5">
                              <h2 className="text-2xl font-black text-white tracking-tight sm:text-3xl">
                                {outlook.label}
                              </h2>
                              <span className="text-2xl font-extrabold text-[#A855F7]">
                                {getDirectionArrow(outlook.current_bias)}
                              </span>
                            </div>
                          </div>

                          <div className="mt-2 sm:mt-0">
                            <p className="text-[10px] font-medium uppercase tracking-[0.22em] text-white/35 mb-1 text-left sm:text-right">
                              {isPortuguese ? 'EXPOSIÇÃO COMPATÍVEL' : 'COMPATIBLE EXPOSURE'}
                            </p>
                            <span className="inline-flex px-3 py-1 rounded-full text-xs font-extrabold border border-purple-500/30 bg-purple-500/10 text-white leading-none">
                              {getLocalizedExposure(outlook.exposure)}
                            </span>
                          </div>
                        </div>

                        {/* Description */}
                        <div className="text-base text-white/80 leading-relaxed font-sans font-medium">
                          {getLocalizedDescription(outlook.current_bias)}
                        </div>

                        {/* Possible Strategies */}
                        <div className="space-y-3 rounded-2xl border border-white/5 bg-white/[0.02] p-5">
                          <p className="text-[10px] font-medium uppercase tracking-[0.22em] text-white/35 leading-none">
                            {isPortuguese ? 'ESTRATÉGIAS POSSÍVEIS' : 'POSSIBLE STRATEGIES'}
                          </p>
                          <div className="grid gap-2.5 sm:grid-cols-3 pt-1">
                            {outlook.possible_strategies.map((strategy, idx) => (
                              <div 
                                key={idx} 
                                className="rounded-[14px] border border-white/5 bg-zinc-950/30 px-3.5 py-3 flex items-center gap-2 hover:border-white/10 transition-colors"
                              >
                                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#00E676]/10 text-[#00E676] border border-[#00E676]/20 text-[10px] font-bold">
                                  ✓
                                </span>
                                <span className="text-xs font-bold text-white/80">
                                  {getLocalizedStrategy(strategy)}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Futures Hedge Section */}
                        <div className="space-y-4 pt-4 border-t border-white/5" data-testid="futures-hedge-section">
                          <div className="space-y-1">
                            <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-[#A855F7] flex items-center gap-1.5">
                              <span>FUTURES HEDGE</span>
                              <span className="text-[10px] text-white/30 lowercase font-medium tracking-normal">
                                ({isPortuguese ? 'níveis de referência · não é sinal de entrada' : 'reference levels · not an entry signal'})
                              </span>
                            </h3>
                            <p className="text-sm text-white/55 leading-relaxed">
                              {isPortuguese 
                                ? 'Níveis de referência definidos pelo time nos quais um hedge direcional via BTC Futures pode ser considerado para proteção das posições de opções.'
                                : 'Reference levels defined by the team where a directional hedge via BTC Futures can be considered to protect options positions.'}
                            </p>
                          </div>

                          <div className="grid gap-4 sm:grid-cols-2 pt-1">
                            {/* Put Side */}
                            <div className="rounded-[16px] border border-white/5 bg-zinc-950/20 p-4 space-y-3">
                              <p className="text-xs font-black text-red-400 tracking-wider">
                                PUT SIDE ({isPortuguese ? 'PROTEÇÃO DE QUEDA' : 'DOWNSIDE PROTECTION'})
                              </p>
                              
                              {outlook.put_hedge_aggressive || outlook.put_hedge_conservative ? (
                                <div className="grid grid-cols-2 gap-3 text-xs">
                                  {outlook.put_hedge_aggressive && (
                                    <div className="rounded-[12px] border border-white/5 bg-zinc-950/40 p-3">
                                      <p className="text-[9px] uppercase tracking-[0.16em] text-white/35 mb-1 leading-none">{isPortuguese ? 'Hedge Agressivo' : 'Aggressive Hedge'}</p>
                                      <p className="text-sm font-bold text-white tabular-nums">${outlook.put_hedge_aggressive.toLocaleString()}</p>
                                    </div>
                                  )}
                                  {outlook.put_hedge_conservative && (
                                    <div className="rounded-[12px] border border-white/5 bg-zinc-950/40 p-3">
                                      <p className="text-[9px] uppercase tracking-[0.16em] text-white/35 mb-1 leading-none">{isPortuguese ? 'Hedge Conservador' : 'Conservative Hedge'}</p>
                                      <p className="text-sm font-bold text-white tabular-nums">${outlook.put_hedge_conservative.toLocaleString()}</p>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <p className="text-xs text-white/40 italic">
                                  {isPortuguese ? 'Nenhum nível de hedge via futures identificado no momento.' : 'No futures hedge levels identified at this time.'}
                                </p>
                              )}
                            </div>

                            {/* Call Side */}
                            <div className="rounded-[16px] border border-white/5 bg-zinc-950/20 p-4 space-y-3">
                              <p className="text-xs font-black text-emerald-400 tracking-wider">
                                CALL SIDE ({isPortuguese ? 'PROTEÇÃO DE ALTA' : 'UPSIDE PROTECTION'})
                              </p>
                              
                              {outlook.call_hedge_aggressive || outlook.call_hedge_conservative ? (
                                <div className="grid grid-cols-2 gap-3 text-xs">
                                  {outlook.call_hedge_aggressive && (
                                    <div className="rounded-[12px] border border-white/5 bg-zinc-950/40 p-3">
                                      <p className="text-[9px] uppercase tracking-[0.16em] text-white/35 mb-1 leading-none">{isPortuguese ? 'Hedge Agressivo' : 'Aggressive Hedge'}</p>
                                      <p className="text-sm font-bold text-white tabular-nums">${outlook.call_hedge_aggressive.toLocaleString()}</p>
                                    </div>
                                  )}
                                  {outlook.call_hedge_conservative && (
                                    <div className="rounded-[12px] border border-white/5 bg-zinc-950/40 p-3">
                                      <p className="text-[9px] uppercase tracking-[0.16em] text-white/35 mb-1 leading-none">{isPortuguese ? 'Hedge Conservador' : 'Conservative Hedge'}</p>
                                      <p className="text-sm font-bold text-white tabular-nums">${outlook.call_hedge_conservative.toLocaleString()}</p>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <p className="text-xs text-white/40 italic">
                                  {isPortuguese ? 'Nenhum nível de hedge via futures identificado no momento.' : 'No futures hedge levels identified at this time.'}
                                </p>
                              )}
                            </div>
                          </div>

                          <p className="text-[10px] text-white/35 leading-relaxed bg-zinc-950/30 px-3.5 py-2.5 rounded-lg border border-white/5">
                            {isPortuguese 
                              ? 'Os níveis de hedge são definidos a partir da análise conjunta de GEX, análise técnica, order flow e liquidez de mercado.'
                              : 'Hedge levels are defined from the joint analysis of GEX, technical analysis, order flow, and market liquidity.'}
                          </p>
                        </div>

                        {/* Four Pilars */}
                        {renderFourPilars()}

                        {/* Footer info */}
                        <div className="flex flex-col gap-2 pt-4 border-t border-white/5 text-[10px] text-white/35 sm:flex-row sm:items-center sm:justify-end">
                          <span>
                            {isPortuguese ? 'ÚLTIMA ATUALIZAÇÃO' : 'LAST UPDATED'} · <span className="font-semibold text-white/55">{formatLastUpdatedDate(outlook.updated_at)}</span>
                          </span>
                        </div>

                      </CardContent>
                    </Card>
                  ) : (
                    /* FREE EXPERIENCIA - LOCKED */
                    <div className="space-y-6">
                      <Card 
                        onClick={() => openUpgradeModal("BTC_OUTLOOK")}
                        className="relative border border-white/5 bg-zinc-950/40 backdrop-blur-md rounded-[24px] text-white overflow-hidden shadow-none cursor-pointer group"
                      >
                        {/* Locked Overlay */}
                        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center p-6 text-center bg-[#09090B]/85 rounded-[24px] backdrop-blur-[3px]">
                          <div className="mx-auto max-w-sm space-y-4">
                            <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-[#A855F7]/10 text-[#A855F7] border border-[#A855F7]/30 animate-pulse">
                              <Lock size={26} weight="fill" />
                            </div>
                            <div>
                              <p className="text-lg font-black text-white">GENI Options PRO 💜</p>
                              <p className="mt-1.5 text-xs text-white/55 leading-relaxed">
                                {isPortuguese 
                                  ? 'Descubra o viés de exposição ao Bitcoin e níveis de proteção de Futures Hedge para os próximos 30–60 dias.'
                                  : 'Discover the current Bitcoin exposure bias and Futures Hedge protection levels for the next 30–60 days.'}
                              </p>
                            </div>
                            <Button 
                              onClick={(e) => {
                                e.stopPropagation();
                                openUpgradeModal("BTC_OUTLOOK");
                              }}
                              className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-bold text-xs px-6 py-3 rounded-xl shadow-lg shadow-purple-500/10 transition-all transform hover:-translate-y-0.5 active:translate-y-0"
                            >
                              {isPortuguese ? 'DESBLOQUEAR OUTLOOK' : 'UNLOCK OUTLOOK'}
                            </Button>
                          </div>
                        </div>

                        <CardContent className="p-6 sm:p-8 space-y-6 blur-[3px] select-none pointer-events-none">
                          {/* Dummy background layout representing locked content */}
                          <div className="flex items-center justify-between border-b border-white/5 pb-4">
                            <div className="space-y-1">
                              <p className="text-[10px] tracking-[0.2em] text-white/35">
                                {isPortuguese ? 'VIÉS DE MERCADO · 30–60 DIAS' : 'MARKET BIAS · 30–60 DAYS'}
                              </p>
                              <h2 className="text-2xl font-bold">SLIGHTLY BULLISH ↗</h2>
                            </div>
                            <div className="text-right">
                              <p className="text-[10px] tracking-[0.2em] text-white/35">
                                {isPortuguese ? 'EXPOSIÇÃO COMPATÍVEL' : 'COMPATIBLE EXPOSURE'}
                              </p>
                              <span className="px-2.5 py-0.5 border border-purple-500/20 bg-purple-500/10 rounded-full text-xs">
                                {isPortuguese ? 'Moderadamente Positiva' : 'Slightly Positive'}
                              </span>
                            </div>
                          </div>
                          <div className="h-10 bg-white/5 rounded-lg w-full"></div>
                          <div className="h-20 bg-white/5 rounded-lg w-full"></div>
                          {renderFourPilars()}
                        </CardContent>
                      </Card>

                      {/* Futures Hedge Section for FREE Users */}
                      <Card 
                        onClick={() => openUpgradeModal("BTC_OUTLOOK")}
                        className="border border-white/5 bg-zinc-950/40 backdrop-blur-md rounded-[24px] text-white overflow-hidden shadow-none cursor-pointer group hover:bg-zinc-950/60 transition-all duration-300"
                      >
                        <CardContent className="p-6 sm:p-8 space-y-4">
                          <div className="space-y-1">
                            <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-[#A855F7] flex items-center gap-1.5">
                              <span>FUTURES HEDGE</span>
                              <span className="text-[10px] text-white/30 lowercase font-medium tracking-normal">
                                ({isPortuguese ? 'níveis de referência · não é sinal de entrada' : 'reference levels · not an entry signal'})
                              </span>
                              <Lock size={11} className="text-[#A855F7]" weight="fill" />
                            </h3>
                            <p className="text-sm text-white/55 leading-relaxed">
                              {isPortuguese 
                                ? 'Níveis de referência definidos pelo time nos quais um hedge direcional via BTC Futures pode ser considerado para proteção das posições de opções.'
                                : 'Reference levels defined by the team where a directional hedge via BTC Futures can be considered to protect options positions.'}
                            </p>
                          </div>

                          <div className="grid gap-4 sm:grid-cols-2 pt-1 filter blur-[2px] opacity-40 select-none pointer-events-none">
                            {/* Put Side */}
                            <div className="rounded-[16px] border border-white/5 bg-zinc-950/20 p-4 space-y-3">
                              <p className="text-xs font-black text-red-400 tracking-wider">PUT SIDE</p>
                              <div className="grid grid-cols-2 gap-3 text-xs">
                                <div className="rounded-[12px] border border-white/5 bg-zinc-950/40 p-3">
                                  <p className="text-[9px] uppercase tracking-[0.16em] text-white/35 mb-1 leading-none">{isPortuguese ? 'Hedge Agressivo' : 'Aggressive Hedge'}</p>
                                  <p className="text-sm font-bold text-white tabular-nums">$••••••</p>
                                </div>
                                <div className="rounded-[12px] border border-white/5 bg-zinc-950/40 p-3">
                                  <p className="text-[9px] uppercase tracking-[0.16em] text-white/35 mb-1 leading-none">{isPortuguese ? 'Hedge Conservador' : 'Conservative Hedge'}</p>
                                  <p className="text-sm font-bold text-white tabular-nums">$••••••</p>
                                </div>
                              </div>
                            </div>
                            
                            {/* Call Side */}
                            <div className="rounded-[16px] border border-white/5 bg-zinc-950/20 p-4 space-y-3">
                              <p className="text-xs font-black text-emerald-400 tracking-wider">CALL SIDE</p>
                              <div className="grid grid-cols-2 gap-3 text-xs">
                                <div className="rounded-[12px] border border-white/5 bg-zinc-950/40 p-3">
                                  <p className="text-[9px] uppercase tracking-[0.16em] text-white/35 mb-1 leading-none">{isPortuguese ? 'Hedge Agressivo' : 'Aggressive Hedge'}</p>
                                  <p className="text-sm font-bold text-white tabular-nums">$••••••</p>
                                </div>
                                <div className="rounded-[12px] border border-white/5 bg-zinc-950/40 p-3">
                                  <p className="text-[9px] uppercase tracking-[0.16em] text-white/35 mb-1 leading-none">{isPortuguese ? 'Hedge Conservador' : 'Conservative Hedge'}</p>
                                  <p className="text-sm font-bold text-white tabular-nums">$••••••</p>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="text-[10px] text-white/35 leading-relaxed bg-zinc-950/30 px-3.5 py-2.5 rounded-lg border border-white/5 flex items-center justify-between gap-4">
                            <span>
                              {isPortuguese 
                                ? 'Os níveis de hedge são definidos a partir da análise conjunta de GEX, análise técnica, order flow e liquidez de mercado.'
                                : 'Hedge levels are defined from the joint analysis of GEX, technical analysis, order flow, and market liquidity.'}
                            </span>
                            <span className="text-[#A855F7] font-black uppercase tracking-wider text-[9px] flex items-center gap-1 shrink-0">
                              {isPortuguese ? 'DESBLOQUEAR PRO' : 'UNLOCK PRO'} <ArrowRight size={10} />
                            </span>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  )}
                </>
              ) : (
                /* UNPUBLISHED STATE (No outlook published yet) */
                <Card className="border border-white/5 bg-zinc-950/40 backdrop-blur-md rounded-[24px] text-white shadow-none">
                  <CardContent className="p-8 sm:p-12 text-center space-y-4">
                    <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-zinc-900 border border-white/5 text-white/35">
                      <Compass size={28} weight="bold" />
                    </div>
                    <div className="space-y-1.5 max-w-md mx-auto">
                      <h3 className="text-lg font-black text-white">
                        {isPortuguese ? 'Aguardando publicação' : 'Outlook Pending'}
                      </h3>
                      <p className="text-sm text-white/52 leading-relaxed">
                        {outlook?.message || (isPortuguese 
                          ? 'Nenhuma leitura de mercado foi publicada pelo administrador ainda. Fique atento para atualizações em breve!'
                          : 'No market outlook has been published by the administrator yet. Stay tuned for updates shortly!')}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}

            </div>
          )}

        </div>
      </div>
    </OptionsPageShell>
  );
}
